import React, { useCallback, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Alert, Button, Col, Container, FormControl, InputGroup, Row, Table } from 'react-bootstrap';
import axios from 'axios';

const UITest = ({ onSendSelectedStrikes, indices, strikeData, configs, tradingEnabled }) => {
    const [statusMsg, setStatusMsg] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(indices[0] || 'NIFTY');
    const [currentStrikePrice, setCurrentStrikePrice] = useState(null);
    const [currentStrikePriceRounded, setCurrentStrikePriceRounded] = useState(null);
    const [rowData, setRowData] = useState([]);
    const [defaultLotSize, setDefaultLotSize] = useState(50);
    const [isLtpLoading, setIsLtpLoading] = useState(false);
    const [maxLots, setMaxLots] = useState(1);

    useEffect(() => {
        setDefaultLotSize(configs[selectedIndex]?.lotSize);
        setMaxLots(configs[selectedIndex]?.maxLots);
        // Validate workingFigure
        if (configs[selectedIndex]?.workingFigure === null) {
            setStatusMsg(`Working Figure is not set for ${selectedIndex}. Please set it in the index configuration.`);
        }
    }, [selectedIndex, configs]);

    const handleFetchCurrentStrikePrice = useCallback(async () => {
        try {
            setLoading(true);
            setStatusMsg(`Fetching current strike price for ${selectedIndex}`);
            const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/indices/${selectedIndex}/market-data`);
            if (response.data && response.data.ltp) {
                const price = response.data.ltp;
                const step = configs[selectedIndex]?.step || 50;
                const roundedPrice = Math.round(price / step) * step;
                setCurrentStrikePrice(price);
                setCurrentStrikePriceRounded(roundedPrice);
                setStatusMsg(`Fetched strike price: ${price} (rounded: ${roundedPrice})`);
            } else {
                setStatusMsg(`Failed to fetch strike price for ${selectedIndex}`);
            }
        } catch (error) {
            console.error('Error fetching strike price:', error);
            setStatusMsg(`Error fetching strike price: ${error.response?.data || error.message}`);
        } finally {
            setLoading(false);
        }
    }, [selectedIndex, configs]);

const fetchAllLtps = useCallback(async () => {
    if (rowData.length === 0) {
        setStatusMsg('No rows to fetch LTPs');
        return;
    }
    try {
        setIsLtpLoading(true);
        setStatusMsg(`Fetching LTP data for ${selectedIndex} options...`);
        const newRows = [...rowData];
        
        // Sleep function to introduce delay
        const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
        
        for (let i = 0; i < newRows.length; i++) {
            if (newRows[i].ceItem && !newRows[i].isExactStrike) {
                const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/indices/${selectedIndex}/market-data`, {
                    params: { symbol: newRows[i].ceItem.symbol, token: newRows[i].ceItem.token },
                });
                if (response.data?.ltp) newRows[i].ceLtp = response.data.ltp;
                await sleep(100); // 100ms delay to respect 10 calls/second limit
            }
            if (newRows[i].peItem && !newRows[i].isExactStrike) {
                const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/indices/${selectedIndex}/market-data`, {
                    params: { symbol: newRows[i].peItem.symbol, token: newRows[i].peItem.token },
                });
                if (response.data?.ltp) newRows[i].peLtp = response.data.ltp;
                await sleep(100); // 100ms delay to respect 10 calls/second limit
            }
        }
        setRowData(newRows);
        setStatusMsg('LTP data updated successfully');
    } catch (error) {
        console.error('Error fetching LTPs:', error);
        setStatusMsg(`Error fetching LTPs: ${error.response?.data || error.message}`);
    } finally {
        setIsLtpLoading(false);
    }
}, [selectedIndex, rowData]);

    useEffect(() => {
        if (!currentStrikePrice || !strikeData[selectedIndex]) {
            return;
        }
        const step = configs[selectedIndex]?.step || 50;
        const rows = [];
        const baseStrike = Math.round(currentStrikePrice / step) * step;
        const exactStrike = currentStrikePrice;

        // Generate 10 standard rows: CE levels -4*step to +5*step, PE levels -5*step to +4*step
        for (let i = -4; i <= 5; i++) {
            const ceLevel = i * step;
            const peLevel = (i - 1) * step; // PE level is CE level - step
            const strikeVal = baseStrike + ceLevel;
            const closestCE = strikeData[selectedIndex].ce.reduce((a, b) =>
                Math.abs(b.strike - strikeVal) < Math.abs(a.strike - strikeVal) ? b : a,
                strikeData[selectedIndex].ce[0]
            );
            const closestPE = strikeData[selectedIndex].pe.reduce((a, b) =>
                Math.abs(b.strike - strikeVal) < Math.abs(a.strike - strikeVal) ? b : a,
                strikeData[selectedIndex].pe[0]
            );
            const isCurrentStrike = i === 0;

            rows.push({
                id: `row_${strikeVal}`,
                ceLevel,
                peLevel,
                strikePrice: strikeVal,
                ceOn: isCurrentStrike && closestCE,
                peOn: i === 1 && closestPE,
                ceOnForTrading: false,
                peOnForTrading: false,
                ceItem: closestCE,
                peItem: closestPE,
                ceStrikePrice: closestCE?.strike || 0,
                peStrikePrice: closestPE?.strike || 0,
                ceLtp: 0,
                peLtp: 0,
                ceNumberOfLots: maxLots,
                peNumberOfLots: maxLots,
                isCurrentStrike,
                isExactStrike: false,
            });
        }

        // Add the exact strike row
        const exactCE = strikeData[selectedIndex].ce.reduce((a, b) =>
            Math.abs(b.strike - exactStrike) < Math.abs(a.strike - exactStrike) ? b : a,
            strikeData[selectedIndex].ce[0]
        );
        const exactPE = strikeData[selectedIndex].pe.reduce((a, b) =>
            Math.abs(b.strike - exactStrike) < Math.abs(a.strike - exactStrike) ? b : a,
            strikeData[selectedIndex].pe[0]
        );

        const exactStrikeRow = {
            id: `row_exact_${exactStrike}`,
            ceLevel: null,
            peLevel: null,
            strikePrice: exactStrike,
            ceOn: false,
            peOn: false,
            ceOnForTrading: false,
            peOnForTrading: false,
            ceItem: exactCE,
            peItem: exactPE,
            ceStrikePrice: exactCE?.strike || 0,
            peStrikePrice: exactPE?.strike || 0,
            ceLtp: 0,
            peLtp: 0,
            ceNumberOfLots: 1,
            peNumberOfLots: 1,
            isCurrentStrike: false,
            isExactStrike: true,
        };

        // Insert exact strike row betweenn CE level 0 and CE level +50
        const insertIndex = rows.findIndex(row => row.ceLevel === 0) + 1;
        rows.splice(insertIndex, 0, exactStrikeRow);

        setRowData(rows);
        fetchAllLtps();
    }, [currentStrikePrice, selectedIndex, strikeData, maxLots]);

    // CE-to-PE row index mapping (0-based): Row 1 (i=0) maps to Row 11 (i=10), etc.
    const ceToPeRowIndexMap = {
        0: 10, // Row 1 (CE -200) → Row 11 (PE +200)
        1: 9,  // Row 2 (CE -150) → Row 10 (PE +150)
        2: 8,  // Row 3 (CE -100) → Row 9 (PE +100)
        3: 7,  // Row 4 (CE -50) → Row 8 (PE +50)
        4: 6,  // Row 5 (CE 0) → Row 7 (PE 0)
        6: 4,  // Row 7 (CE +50) → Row 5 (PE -50)
        7: 3,  // Row 8 (CE +100) → Row 4 (PE -100)
        8: 2,  // Row 9 (CE +150) → Row 3 (PE -150)
        9: 1,  // Row 10 (CE +200) → Row 2 (PE -200)
        10: 0, // Row 11 (CE +250) → Row 1 (PE -250)
    };

    const toggleCE = (index) => {
        setRowData((prev) =>
            prev.map((row, i) => {
                if (i === index) {
                    const newCeOnForTrading = !row.ceOnForTrading;
                    return { ...row, ceOnForTrading: newCeOnForTrading };
                }
                return row;
            })
        );

        // Auto-toggle corresponding PE row
        if (ceToPeRowIndexMap[index] !== undefined) {
            setRowData((prev) =>
                prev.map((row, i) => {
                    if (i === ceToPeRowIndexMap[index]) {
                        return {
                            ...row,
                            peOnForTrading: prev[index].ceOnForTrading, // Sync with CE's previous state
                            peNumberOfLots: prev[index].ceNumberOfLots, // Sync lot size
                        };
                    }
                    return row;
                })
            );
        }
    };

    const togglePE = (index) => {
        setRowData((prev) =>
            prev.map((row, i) =>
                i === index ? { ...row, peOnForTrading: !row.peOnForTrading } : row
            )
        );
    };

    const handleLotSizeChange = (index, value) => {
        setRowData((prev) =>
            prev.map((row, i) => {
                if (i === index) {
                    return {
                        ...row,
                        ceNumberOfLots: value,
                        peNumberOfLots: value,
                    };
                }
                return row;
            })
        );

        // Sync lot size to corresponding PE row
        if (ceToPeRowIndexMap[index] !== undefined) {
            setRowData((prev) =>
                prev.map((row, i) => {
                    if (i === ceToPeRowIndexMap[index]) {
                        return { ...row, peNumberOfLots: value };
                    }
                    return row;
                })
            );
        }
    };
    const handleWorkingFigureChange = async (value) => {
        const newValue = Number(value);
        if (newValue < 0) {
            setStatusMsg('Working Figure must be a positive number');
            return
        }
        try {
            await axios.put(`${process.env.REACT_APP_SERVER_URL}/indices/${selectedIndex}`, {
                config: { ...configs[selectedIndex], workingFigure: newValue },
            });
            configs[selectedIndex].workingFigure = newValue; //Update local configs
            setStatusMsg(`Working Figure updated to ${newValue.toLocaleString('en-IN')} for ${selectedIndex}`);
        } catch (error) {
            setStatusMsg(`Error updating Working Figure: ${error.response?.data || error.message}`);
        }
    };

    const calculateTotalInvestment = useCallback(() => {
        return rowData.reduce((total, row) => {
            if ((row.ceOn || row.ceOnForTrading) && row.ceLtp > 0) {
                total += defaultLotSize * row.ceLtp * 1;
            }
            return total;
        }, 0);
    }, [rowData, defaultLotSize]);

    const calculateAmount = (row) => {
        let total = 0;
        if ((row.ceOn || row.ceOnForTrading) && row.ceLtp > 0) {
            total += defaultLotSize * row.ceLtp * row.ceNumberOfLots;
        }
        return total;
    };

    const calculateTotalFigure = useCallback(() => {
        return rowData.reduce((total, row) => {
            if ((row.ceOn || row.ceOnForTrading) && row.ceLtp > 0) {
                total += defaultLotSize * row.ceLtp * row.ceNumberOfLots;
            }
            return total;
        }, 0);
    }, [rowData, defaultLotSize]);

    const workingFigure = configs[selectedIndex]?.workingFigure;
    const unusedFigure = workingFigure !=null ? workingFigure - calculateTotalFigure() : 0;
    const bufferFigure = calculateTotalFigure() * 1.2;

    const sendToggledStrikesToWelcome = () => {
        if (!tradingEnabled[selectedIndex]) {
            setStatusMsg(`Trading disabled for ${selectedIndex}, cannot send levels`);
            return;
        }
        if (workingFigure == null || workingFigure <= 0) {
            setStatusMsg(`Working Figure is not set or invalid for ${selectedIndex}`);
            return;
        }
        const storedCeLevels = rowData
            .filter((row) => row.ceOnForTrading)
            .map((row) => ({ type: 'CE', level: row.ceLevel, numberOfLots: row.ceNumberOfLots }));
        const storedPeLevels = rowData
            .filter((row) => row.peOnForTrading)
            .map((row) => ({ type: 'PE', level: row.peLevel, numberOfLots: row.peNumberOfLots }));
        onSendSelectedStrikes(storedCeLevels, storedPeLevels, selectedIndex, workingFigure);
        setStatusMsg(`Selected ${selectedIndex} CE and PE Levels sent to trading system!`);
    };

  return (
    <Container className='mt-4 bg-white rounded shadow p-4'>
        {statusMsg && <Alert variant='warning' dismissible>{statusMsg}</Alert>}

        <Row className='text-center mb-4'>
            <Col><h3>{selectedIndex} Investment Calculation</h3></Col>
        </Row>

        <Row className='mb-3'>
            <Col md={4}>
                <InputGroup>
                <InputGroup.Text>Index</InputGroup.Text>
                    <FormControl
                        as="select"
                        value={selectedIndex}
                        onChange={(e) => {
                            setSelectedIndex(e.target.value)
                            setCurrentStrikePrice(null);
                            setRowData([]);
                        }}
                    >
                        {indices.map((idx) => <option key={idx} value={idx}>{idx}</option>)}
                    </FormControl>
                </InputGroup>
            </Col>
        </Row>

        <div className='mb-3'>
            <Button
                variant='primary'
                onClick={handleFetchCurrentStrikePrice}
                disabled={loading || !tradingEnabled[selectedIndex]}
                className='me-3'
            >
                {loading ? 'Fetching...' : 'Fetch Price'}
            </Button>
            <Button
                variant='success'
                onClick={fetchAllLtps}
                disabled={isLtpLoading || rowData.length === 0 || !tradingEnabled[selectedIndex]}
                className='me-3'
            >
                {isLtpLoading ? 'Updating LTPs...' : 'Fetch LTP Data'}
            </Button>
            <span style={{ fontWeight: 'bold' }}>
                Current Strike Price: {currentStrikePrice ?? '--'} &emsp;
                Rounded: {currentStrikePriceRounded ?? '--'}
            </span>
        </div>

        <InputGroup className='mb-3' style={{ width: '200px' }}>
            <InputGroup.Text>Lot Size:</InputGroup.Text>
            <FormControl
                type='Number'
                value={defaultLotSize ?? ''}
                onChange={(e) => setDefaultLotSize(Number(e.target.value))}
            />
        </InputGroup>

        <div className='table-responsive'>
            <Table className='table-bordered text-center'>
                <thead>
                    <tr className='table-info'>
                        <th>CE Toggle</th>
                        <th>CE LTP</th>
                        <th>Lot Size</th>
                        <th>Amount</th>
                        <th>Level</th>
                        <th>Strike Price</th>
                        <th>Level</th>
                        <th>PE LTP</th>
                        <th>PE Toggle</th>
                    </tr>
                </thead>

                <tbody>
                    {rowData.map((row, index) => (
                        <tr key={row.id} className={row.isCurrentStrike ? 'bg-info' : row.isExactStrike ? 'bg-warning' : '' }>
                            <td>
                                {!row.isExactStrike && (
                                    <Button
                                        variant={(row.ceOn || row.ceOnForTrading) ? 'success' : 'light'}
                                        size='sm'
                                        onClick={() => toggleCE(index)}
                                        disabled={!tradingEnabled[selectedIndex]}
                                    >
                                        {(row.ceOn || row.ceOnForTrading) ? 'On' : 'Off'}
                                    </Button>
                                )}
                            </td>
                            <td>{!row.isExactStrike ? (row.ceLtp > 0 ? row.ceLtp.toFixed(2) : isLtpLoading ? 'Loading...' : '--') : ''}</td>
                            <td>
                                {!row.isExactStrike && (
                                    <FormControl
                                        type='number'
                                        size='sm'
                                        value={row.ceNumberOfLots}
                                        onChange={(e) => handleLotSizeChange(index, Number(e.target.value))}
                                        style={{ width: "70px", margin: "0 auto" }}
                                    />
                                )}
                            </td>
                            <td>
                                {!row.isExactStrike ? calculateAmount(row).toLocaleString("en-IN") : ""}
                            </td>
                            <td>{row.isExactStrike ? "Exact" : row.ceLevel !== null ? (row.ceLevel >= 0 ? `+${row.ceLevel}` : row.ceLevel) : "--"}</td>
                            <td className={row.isExactStrike ? 'text-light bg-success' : ''}>
                                {row.strikePrice}
                                {row.isExactStrike && <span> (Strike Price)</span>}
                            </td>
                            <td>{row.isExactStrike ? "Exact" : row.peLevel !== null ? (row.peLevel >= 0 ? `+${row.peLevel}` : row.peLevel) : "--"}</td>
                            <td>{!row.isExactStrike ? (row.peLtp > 0 ? row.peLtp.toFixed(2) : isLtpLoading ? 'Loading...' : '--') : ''}</td>
                            <td>
                                {!row.isExactStrike && (
                                    <Button
                                        variant={(row.peOn || row.peOnForTrading) ? 'success' : 'light'}
                                        size='sm'
                                        onClick={() => togglePE(index)}
                                        disabled={!tradingEnabled[selectedIndex]}
                                    >
                                        {(row.peOn || row.peOnForTrading) ? 'On' : 'Off'}
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                    <tr className='bg-primary text-white font-bold'>
                        <td colSpan="9">
                            <strong>Total Investment Required:</strong> {calculateTotalInvestment().toLocaleString('en-IN')}
                        </td>
                    </tr>
                </tbody>
            </Table>
        </div>
        
        <div className='mt-4'>
            <h4>Financial Figures</h4>
            <Table className='table-bordered text-center'>
                <thead>
                    <tr className='table-info'>
                        <th>Category</th>
                        <th>Amount (₹)</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className='text-start'>Working Figure</td>
                        <td>
                            <FormControl
                                type='Text'
                                value={workingFigure ?? ''}
                                onChange={(e) => handleWorkingFigureChange(e.target.value)}
                                style={{ width: "150px", margin: "0 auto" }}
                                disabled={!tradingEnabled[selectedIndex]}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className='text-start'>Calculated Figure</td>
                        <td>{calculateTotalFigure().toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>
                        <td className='text-start'>Unused Figure</td>
                        <td>{unusedFigure.toLocaleString("en-IN")}</td>
                    </tr>
                    <tr>

                        <td className='text-start'>Buffer (20%)</td>
                        <td>{bufferFigure.toLocaleString("en-IN")}</td>
                    </tr>
                </tbody>
            </Table>
        </div>

        <Button
            variant='warning'
            onClick={sendToggledStrikesToWelcome}
            disabled={!tradingEnabled[selectedIndex] || !rowData.some((row) => row.ceOnForTrading || row.peOnForTrading) ||
            workingFigure == null || workingFigure <= 0}
            className='mt-0'
        >
            Confirm & Send
        </Button>
    </Container>
  );
};

export default UITest;